import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { researchStudyService } from '../services/researchStudyService'
import { ResearchStudy } from '../types/researchStudy'
import './ResearchStudies.css'

export function ResearchStudies() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [filterStatus, setFilterStatus] = useState<string>('all')

  const { data: studies, isLoading, error } = useQuery<ResearchStudy[]>({
    queryKey: ['research-studies', filterStatus],
    queryFn: () => researchStudyService.getAll(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => researchStudyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['research-studies'] })
    },
  })

  const filteredStudies = studies?.filter((s) => filterStatus === 'all' || s.status === filterStatus) || []

  if (isLoading) return <div className="research-studies-loading">Loading research studies...</div>
  if (error) return <div className="research-studies-error">Error loading research studies</div>

  return (
    <div className="research-studies-page">
      <div className="page-header">
        <h1>Research Studies</h1>
        <button className="btn-primary" onClick={() => navigate('/research-studies/new')}>
          New Study
        </button>
      </div>

      <div className="studies-filters">
        <button
          className={filterStatus === 'all' ? 'active' : ''}
          onClick={() => setFilterStatus('all')}
        >
          All
        </button>
        <button
          className={filterStatus === 'recruiting' ? 'active' : ''}
          onClick={() => setFilterStatus('recruiting')}
        >
          Recruiting
        </button>
        <button
          className={filterStatus === 'active' ? 'active' : ''}
          onClick={() => setFilterStatus('active')}
        >
          Active
        </button>
        <button
          className={filterStatus === 'completed' ? 'active' : ''}
          onClick={() => setFilterStatus('completed')}
        >
          Completed
        </button>
      </div>

      <div className="studies-list">
        {filteredStudies.length === 0 ? (
          <div className="empty-state">
            <p>No research studies found</p>
            <button className="btn-primary" onClick={() => navigate('/research-studies/new')}>
              Create First Study
            </button>
          </div>
        ) : (
          filteredStudies.map((study) => (
            <div key={study.id} className="study-card">
              <div className="study-header">
                <h3>{study.name}</h3>
                <span className={`status-badge status-${study.status}`}>{study.status}</span>
              </div>
              {study.description && <p className="study-description">{study.description}</p>}
              <div className="study-meta">
                {study.principalInvestigatorName && (
                  <span>PI: {study.principalInvestigatorName}</span>
                )}
                {study.irbApprovalNumber && <span>IRB: {study.irbApprovalNumber}</span>}
                {study.enrollmentTarget && (
                  <span>Target: {study.enrollmentTarget} participants</span>
                )}
              </div>
              <div className="study-actions">
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/research-studies/${study.id}`)}
                >
                  View
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/research-studies/${study.id}/enrollments`)}
                >
                  Enrollments
                </button>
                <button
                  className="btn-danger"
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this study?')) {
                      deleteMutation.mutate(study.id)
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
